import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  MusicNote,
  Share,
} from '@mui/icons-material';
import { AuditioBackendManager, AudioFile } from './AuditioBackend';
import { useAuth } from './contexts/AuthContext';
import DeletionInfoIcon from './components/DeletionInfoIcon';
import DriveShareDialog from './components/DriveShareDialog';
import { GoogleDriveService } from './services/GoogleDriveService';
import { SessionManager } from './SessionManager';
import { getTrackType, renderTrackTypeIcon } from './utils/trackTypeUtils';

interface UserFilesViewProps {
  onFileSelect: (fileId: number) => void;
  auditioBackendManager: AuditioBackendManager;
  refreshTrigger?: number;
}

const UserFilesView: React.FC<UserFilesViewProps> = ({
  onFileSelect,
  auditioBackendManager,
  refreshTrigger,
}) => {
  const { firebaseUser } = useAuth();
  const [userFiles, setUserFiles] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareSnackbar, setShowShareSnackbar] = useState(false);
  const [showDriveShareDialog, setShowDriveShareDialog] = useState(false);
  const [driveFileData, setDriveFileData] = useState<{fileId: string, fileUrl?: string} | null>(null);
  const [pendingFileId, setPendingFileId] = useState<number | null>(null);

  useEffect(() => {
    if (firebaseUser) {
      loadUserFiles();
    } else {
      setUserFiles([]);
      setLoading(false);
    }
  }, [firebaseUser, refreshTrigger]);

  const loadUserFiles = async () => {
    if (!firebaseUser || !auditioBackendManager) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Force token refresh to ensure we have a valid token
      console.log('🔍 UserFilesView - Getting fresh ID token...');
      const idToken = await firebaseUser.getIdToken(true);
      console.log('🔍 UserFilesView - Token obtained, length:', idToken.length);
      console.log('🔍 UserFilesView - Fetching user files...');
      let files = await auditioBackendManager.fetchUserFiles(idToken);
      console.log('✅ UserFilesView - Files fetched successfully, count:', files.length);
      
      // Debug: Log file metadata to check track types
      files.forEach(file => {
        console.log('🔍 UserFilesView - File metadata:', {
          id: file.id,
          fileName: file.fileName,
          youtubeUrl: file.youtubeUrl,
          driveFileId: file.driveFileId,
          driveFileUrl: file.driveFileUrl,
          trackType: getTrackType(file).type
        });
      });
      
      // Fetch full metadata for all files to ensure we have accurate track type and owner info
      // This is necessary because /files/my might not return all metadata fields
      console.log('🔍 UserFilesView - Fetching full metadata for all files to ensure accuracy');
      const metadataPromises = files.map(async (file) => {
        try {
          const fullFileData = await auditioBackendManager.getAudioFileById(file.id);
          if (fullFileData) {
            // Update file with full metadata (only if missing or different)
            if (!file.youtubeUrl && fullFileData.youtubeUrl) {
              file.youtubeUrl = fullFileData.youtubeUrl;
            }
            if (!file.driveFileId && fullFileData.driveFileId) {
              file.driveFileId = fullFileData.driveFileId;
            }
            if (!file.driveFileUrl && fullFileData.driveFileUrl) {
              file.driveFileUrl = fullFileData.driveFileUrl;
            }
            if (!file.ownerDisplayName && fullFileData.ownerDisplayName) {
              file.ownerDisplayName = fullFileData.ownerDisplayName;
            }
            console.log('🔍 UserFilesView - Updated file metadata:', {
              id: file.id,
              fileName: file.fileName,
              youtubeUrl: file.youtubeUrl,
              driveFileId: file.driveFileId,
              driveFileUrl: file.driveFileUrl,
              ownerDisplayName: file.ownerDisplayName,
              trackType: getTrackType(file).type
            });
          }
        } catch (error) {
          console.error(`Error fetching metadata for file ${file.id}:`, error);
        }
      });
      await Promise.all(metadataPromises);
      
      // Sort files by last accessed (most recent first)
      // Get lastAccessed from SessionManager, fallback to uploadTime
      const recentFiles = SessionManager.getRecentFiles();
      
      // Sort by lastAccessed descending
      files.sort((a, b) => {
        const aRecent = recentFiles.find(rf => Number(rf.id) === Number(a.id));
        const bRecent = recentFiles.find(rf => Number(rf.id) === Number(b.id));
        const aLastAccessed = aRecent?.lastAccessed ?? (typeof a.uploadTime === 'number' ? a.uploadTime : a.uploadTime.getTime());
        const bLastAccessed = bRecent?.lastAccessed ?? (typeof b.uploadTime === 'number' ? b.uploadTime : b.uploadTime.getTime());
        return bLastAccessed - aLastAccessed;
      });
      
      setUserFiles(files);
    } catch (err) {
      console.error('❌ UserFilesView - Error loading user files:', err);
      console.error('❌ UserFilesView - Error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(err instanceof Error ? err.message : 'Failed to load files');
      setUserFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (fileId: number) => {
    onFileSelect(fileId);
  };

  const handleShare = async (fileId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    
    // Check if this is a Google Drive file
    if (auditioBackendManager) {
      try {
        const fileData = await auditioBackendManager.getAudioFileById(fileId);
        
        if (fileData?.driveFileId) {
          console.log('🔍 Google Drive file detected, checking permissions...');
          
          // Get Google Drive access token
          const driveService = new GoogleDriveService();
          let accessToken: string;
          try {
            accessToken = await driveService.getAccessToken();
          } catch (err) {
            console.error('❌ Failed to get Drive access token:', err);
            // If we can't get token, just copy the link
            const fileUrl = `${window.location.origin}/${fileId}`;
            await navigator.clipboard.writeText(fileUrl);
            setShowShareSnackbar(true);
            return;
          }

          // Check if file is publicly accessible
          try {
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/drive/check-share`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                driveFileId: fileData.driveFileId,
                accessToken: accessToken
              })
            });

            if (response.ok) {
              const shareData = await response.json();
              
              // If not publicly accessible OR if we couldn't check permissions, show dialog
              if (!shareData.isPubliclyAccessible || !shareData.canShare) {
                // File is not public or we can't verify, show dialog
                setPendingFileId(fileId);
                setDriveFileData({
                  fileId: fileData.driveFileId,
                  fileUrl: shareData.fileUrl || fileData.driveFileUrl
                });
                setShowDriveShareDialog(true);
                return;
              }
            } else {
              // If check fails, assume file needs to be shared
              console.warn('⚠️ Permission check failed, assuming file needs to be shared');
              setPendingFileId(fileId);
              setDriveFileData({
                fileId: fileData.driveFileId,
                fileUrl: fileData.driveFileUrl
              });
              setShowDriveShareDialog(true);
              return;
            }
          } catch (err) {
            console.error('❌ Error checking file permissions:', err);
            // If check fails, assume file needs to be shared and show dialog
            setPendingFileId(fileId);
            setDriveFileData({
              fileId: fileData.driveFileId,
              fileUrl: fileData.driveFileUrl
            });
            setShowDriveShareDialog(true);
            return;
          }
        }
      } catch (err) {
        console.error('❌ Error checking file data:', err);
        // Continue with normal share if check fails
      }
    }
    
    // Normal share flow (non-Drive file or already public Drive file)
    const fileUrl = `${window.location.origin}/${fileId}`;
    
    try {
      await navigator.clipboard.writeText(fileUrl);
      setShowShareSnackbar(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fileUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareSnackbar(true);
    }
  };

  const formatTime = (timestamp: Date | number) => {
    const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Get last accessed timestamp for a file
  const getLastAccessed = (file: AudioFile): number => {
    const recentFiles = SessionManager.getRecentFiles();
    const recentFile = recentFiles.find(rf => Number(rf.id) === Number(file.id));
    return recentFile?.lastAccessed ?? (typeof file.uploadTime === 'number' ? file.uploadTime : file.uploadTime.getTime());
  };

  // Common container styling
  const containerSx = {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(85, 203, 205, 0.1)',
    border: '1px solid rgba(85, 203, 205, 0.15)',
    width: '100%',
    height: '100%'
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={containerSx}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
        textAlign="center"
      >
        <CircularProgress sx={{ color: 'var(--primary)', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading your files...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={containerSx}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
        textAlign="center"
      >
        <Typography variant="body1" color="error" gutterBottom>
          Error loading files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (userFiles.length === 0) {
    return (
      <Box sx={{ 
        width: '100%', 
        maxWidth: { xs: '100%', md: 'none' },
        mx: { xs: 'auto', md: 0 },
        p: 2 
      }}>
        <Box
          sx={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(85, 203, 205, 0.1)',
            border: '1px solid rgba(85, 203, 205, 0.15)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
            p: 4,
            textAlign: 'center'
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {/* Title */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" component="h2" sx={{ 
              fontFamily: 'Soria, serif', 
              fontWeight: 600,
              fontSize: '1.8rem',
              color: 'var(--text-color)'
            }}>
              Your Files
            </Typography>
          </Box>
          
          <MusicNote sx={{ width: 64, height: 64, color: 'var(--primary)', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '1.2rem',
            fontWeight: 500
          }}>
            You haven't uploaded any files yet
          </Typography>
          <Typography variant="body1" sx={{
            color: 'var(--text-color)',
            mt: 1,
            opacity: 0.8,
            fontSize: '1rem',
            lineHeight: 1.6
          }}>
            Upload an audio file or add a YouTube video to get started.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Files list
  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: { xs: '100%', md: 'none' },
      mx: { xs: 'auto', md: 0 },
      p: 2 
    }}>
      <Box sx={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(85, 203, 205, 0.1)',
        border: '1px solid rgba(85, 203, 205, 0.15)',
        width: '100%',
        p: 3
      }}>
        {/* Title */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" sx={{ 
            fontFamily: 'Soria, serif', 
            fontWeight: 600,
            fontSize: '1.8rem',
            color: 'var(--text-color)'
          }}>
            Your Files
          </Typography>
        </Box>

        {/* Files List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {userFiles.map((file) => (
            <Box 
              key={file.id} 
              className="file-item"
              onClick={() => handleFileSelect(Number(file.id))}
            >
              <Box className="file-header">
                <Box className="file-icon-name" sx={{ flexDirection: 'row', alignItems: 'center', width: 100, gap: 0.75 }}>
                  {renderTrackTypeIcon(getTrackType(file).type, undefined, 36)}
                  <Typography className="file-name">
                    {file.fileName}
                  </Typography>
                </Box>
              </Box>
              <Box className="file-meta">
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }}>
                  By {file.ownerDisplayName || 'Unknown'}
                </Typography>
                <Box className="file-actions">
                  {/* Deletion info icon */}
                  <DeletionInfoIcon 
                    file={{
                      id: Number(file.id),
                      fileName: file.fileName,
                      isUserUploaded: true,
                      uploadTime: typeof file.uploadTime === 'number' ? file.uploadTime : file.uploadTime.getTime(),
                      lastAccessed: typeof file.uploadTime === 'number' ? file.uploadTime : file.uploadTime.getTime()
                    }} 
                    size="small" 
                  />
                  <IconButton
                    className="file-action-btn"
                    onClick={(e) => handleShare(Number(file.id), e)}
                    size="small"
                  >
                    <Share />
                  </IconButton>
                </Box>
              </Box>
              <Box className="file-times">
                <Box className="file-time">
                  <svg viewBox="0 0 24 24">
                    <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                  </svg>
                  Created: {new Date(file.uploadTime).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </Box>
                <Box className="file-time">
                  <svg viewBox="0 0 24 24">
                    <path d="M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9M12,4.5C17,4.5 21.27,7.61 23,12C21.27,16.39 17,19.5 12,19.5C7,19.5 2.73,16.39 1,12C2.73,7.61 7,4.5 12,4.5M3.18,12C4.83,15.36 8.24,17.5 12,17.5C15.76,17.5 19.17,15.36 20.82,12C19.17,8.64 15.76,6.5 12,6.5C8.24,6.5 4.83,8.64 3.18,12Z"/>
                  </svg>
                  Accessed: {formatTime(getLastAccessed(file))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      
      {/* Share Success Snackbar */}
      <Snackbar
        open={showShareSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowShareSnackbar(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Google Drive Share Dialog */}
      <DriveShareDialog
        open={showDriveShareDialog}
        onClose={() => {
          setShowDriveShareDialog(false);
          setDriveFileData(null);
          setPendingFileId(null);
        }}
        onOpenInDrive={() => {
          // Copy the Auditio link first
          if (pendingFileId !== null) {
            const fileUrl = `${window.location.origin}/${pendingFileId}`;
            navigator.clipboard.writeText(fileUrl).then(() => {
              setShowShareSnackbar(true);
            }).catch(() => {
              // Fallback
              const textArea = document.createElement('textarea');
              textArea.value = fileUrl;
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
              setShowShareSnackbar(true);
            });
          }
          
          // Open Google Drive
          if (driveFileData?.fileUrl) {
            window.open(driveFileData.fileUrl, '_blank');
          }
          setShowDriveShareDialog(false);
          setDriveFileData(null);
          setPendingFileId(null);
        }}
        fileUrl={driveFileData?.fileUrl}
      />
    </Box>
  );
};

export default UserFilesView;

